import React, { useState, useEffect } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import VerificationLog from './VerificationLog';
import { fetchLocationData } from './ipinfoService';
import '@aws-amplify/ui-react/styles.css';
import './App.css';

// Configure Amplify with Cognito Identity Pool
Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: 'us-east-1:09d5d355-cb05-4776-a1bb-dd6c06acb229',
      region: 'us-east-1',
      allowGuestAccess: true
    }
  }
});

// Your API endpoints
const API_CREATE_SESSION = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/CreateLivenessSession';
const API_GET_RESULTS = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/GetLivenessResults';

function App() {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [livenessResults, setLivenessResults] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('start'); // start, liveness, results
  
  // Session tracking states
  const [sessionLog, setSessionLog] = useState([]);
  const [linkId] = useState(() => Math.floor(Math.random() * 9000) + 1000);

  // Capture initial page load session
  useEffect(() => {
    const captureInitialSession = async () => {
      const sessionData = await fetchLocationData();
      setSessionLog([sessionData]);
    };
    
    captureInitialSession();
  }, []);

  // Reset flow
  const resetFlow = () => {
    setCurrentStep('start');
    setLivenessResults(null);
    setError(null);
    setSessionId(null);
  };

  // Start liveness verification
  const startVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CREATE_SESSION);
      const data = await response.json();
      
      if (data.sessionId) {
        setSessionId(data.sessionId);
        setCurrentStep('liveness');
      } else {
        setError('Failed to create liveness session');
      }
    } catch (err) {
      setError('Error starting verification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful liveness check
  const handleAnalysisComplete = async () => {
    setLoading(true);
    
    try {
      const livenessResponse = await fetch(API_GET_RESULTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      const livenessData = await livenessResponse.json();
      setLivenessResults(livenessData);
      setSessionId(null);

      // Capture verification complete session
      const completionSession = await fetchLocationData();
      completionSession.completed = true;
      setSessionLog(prev => [...prev, completionSession]);

      setCurrentStep('results');
    } catch (err) {
      setError('Error getting results: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle errors
  const handleError = (error) => {
    console.error('Liveness check error:', error);
    setError('Liveness check failed: ' + error.message);
    setCurrentStep('start');
  };

  return (
    <ThemeProvider>
      <div className="App">
        <header className="App-header">
          <img src="https://idvpacific.com.au/wp-content/uploads/2018/09/IVD-Logo-retina.png" alt="IDV Pacific Logo" className="logo" />
          <h1>Face Liveness Verification</h1>
          <p>Secure face liveness detection</p>
        </header>

        <div className="container">
          {/* Start Screen */}
          {currentStep === 'start' && (
            <div className="start-section">
              <div className="info-card">
                <h2>Welcome to IDV Pacific</h2>
                <p>Complete your face liveness verification:</p>
                <ul className="features-list">
                  <li>👤 Position your face in the oval</li>
                  <li>💡 Ensure good lighting</li>
                  <li>✓ Follow the on-screen instructions</li>
                  <li>⚡ Receive instant verification results</li>
                </ul>
              </div>
              <button 
                onClick={startVerification}
                disabled={loading}
                className="start-button"
              >
                {loading ? 'Starting...' : 'Start Verification'}
              </button>
            </div>
          )}

          {/* Face Liveness */}
          {currentStep === 'liveness' && sessionId && (
            <div className="liveness-section">
              <h3 className="step-title">Face Liveness Verification</h3>
              <FaceLivenessDetector
                sessionId={sessionId}
                region="us-east-1"
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleError}
                disableInstructionScreen={false}
              />
            </div>
          )}

          {/* Results */}
          {currentStep === 'results' && (
            <div className="results-section">
              <div className="result-card">
                <h2>Verification Complete</h2>
                
                {/* Liveness Results */}
                <div className="results-subsection">
                  <h3>Face Liveness Results</h3>
                  <div className="result-status">
                    <div className="status-badge">
                      <span className={`status status-${livenessResults.status?.toLowerCase()}`}>
                        {livenessResults.status === 'SUCCEEDED' ? '✓ Verified' : '✗ Failed'}
                      </span>
                    </div>
                    {livenessResults.confidence !== undefined && (
                      <div className="confidence-display">
                        <span className="confidence-label">Confidence Score</span>
                        <span className="confidence-value">
                          {livenessResults.confidence.toFixed(2)}%
                        </span>
                      </div>
                    )}
                  </div>

                  {livenessResults.referenceImage && (
                    <div className="reference-image-container">
                      <h4>Reference Image</h4>
                      <img 
                        src={`data:image/jpeg;base64,${livenessResults.referenceImage}`}
                        alt="Reference"
                        className="reference-image"
                      />
                    </div>
                  )}
                </div>

                <button 
                  onClick={resetFlow}
                  className="action-button"
                >
                  Verify Another Person
                </button>
              </div>

              {/* Verification Log */}
              <VerificationLog sessions={sessionLog} linkId={linkId} />
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-section">
              <div className="error-card">
                <h3>Verification Error</h3>
                <p className="error-message">{error}</p>
                <button 
                  onClick={resetFlow}
                  className="action-button"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && !sessionId && (
            <div className="loading-section">
              <div className="spinner"></div>
              <p>Processing...</p>
            </div>
          )}
        </div>

        <footer className="App-footer">
          <p>Identity verification powered by <strong>IDV Pacific</strong></p>
          <p className="footer-links">
            <a href="https://idvpacific.com.au/contact/" target="_blank" rel="noopener noreferrer">Contact Us</a>
            {' | '}
            <a href="https://idvpacific.com.au/" target="_blank" rel="noopener noreferrer">About IDV Pacific</a>
          </p>
        </footer>
      </div>
    </ThemeProvider>
  );
}

export default App;